import { useState } from 'react';
import { ClientLogo } from '@/components/client-logo';
import { Link, Navigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  CircleCheck,
  Eye,
  EyeOff,
  Loader2,
  UserPlus,
} from 'lucide-react';
import {
  LanguageMenu,
  LanguageSwitcher,
} from '@/components/language-switcher';
import { ThemeToggle } from '@/components/theme-toggle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { env } from '@/config/env';
import { cn } from '@/lib/utils';
import { useAuthStore } from './auth.store';
import { LoginBrandPanel } from './login-brand-panel';
import { GENDER, LANGUAGE, useRegister } from './register.api';

/**
 * Password rules, declared once so the checklist the user sees and the rule the
 * form enforces can never drift apart.
 */
const PASSWORD_RULES = [
  {
    key: 'auth.register.passwordRules.length',
    test: (v: string) => v.length >= 8,
  },
  {
    key: 'auth.register.passwordRules.uppercase',
    test: (v: string) => /[A-Z]/.test(v),
  },
  {
    key: 'auth.register.passwordRules.lowercase',
    test: (v: string) => /[a-z]/.test(v),
  },
  {
    key: 'auth.register.passwordRules.number',
    test: (v: string) => /\d/.test(v),
  },
];

const schema = z
  .object({
    firstName: z.string().trim().min(1, 'auth.validation.firstNameRequired'),
    lastName: z.string().trim().min(1, 'auth.validation.lastNameRequired'),
    email: z
      .string()
      .trim()
      .min(1, 'auth.validation.emailRequired')
      .email('auth.validation.emailInvalid'),
    phoneNumber: z
      .string()
      .trim()
      .min(1, 'auth.validation.phoneRequired')
      .refine(
        (value) => value.replace(/\D/g, '').length >= 10,
        'auth.validation.phoneInvalid'
      ),
    gender: z.coerce.number(),
    language: z.coerce.number(),
    password: z
      .string()
      .min(1, 'auth.validation.passwordCreate')
      .refine(
        (value) => PASSWORD_RULES.every((rule) => rule.test(value)),
        'auth.validation.passwordWeak'
      ),
    confirmPassword: z.string().min(1, 'auth.validation.confirmRequired'),
  })
  .refine((values) => values.password === values.confirmPassword, {
    // Attach to the field the user must actually change.
    path: ['confirmPassword'],
    message: 'auth.validation.passwordMismatch',
  });

type FormValues = z.infer<typeof schema>;

/* `message` is an i18n key; an unknown key falls through t() unchanged. */
function FieldError({ message }: { message?: string }) {
  const { t } = useTranslation();
  if (!message) return null;
  return <p className="text-destructive text-xs">{t(message)}</p>;
}

/**
 * Request an admin account.
 *
 * The old form was a bare two-column grid of Ant inputs with the submit button
 * disabled until every field held a magic `'warning'` status — so a user with a
 * mistyped phone number saw a dead button and no explanation. It also posted its
 * whole component state to the API. This version validates with the same rules
 * it displays, keeps submit live, and reports what went wrong.
 */
export function RegisterPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const registration = useRegister();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      gender: GENDER.male,
      language: env.defaultLanguage === 'es' ? LANGUAGE.es : LANGUAGE.en,
      password: '',
      confirmPassword: '',
    },
  });

  const password = watch('password');

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  if (registration.isSuccess) {
    return (
      <div className="grid lg:min-h-svh lg:grid-cols-2">
        <div className="flex min-h-[85svh] flex-col items-center justify-center gap-5 p-6 text-center sm:p-10 lg:min-h-0">
          <span className="bg-success/12 text-success flex size-14 items-center justify-center rounded-full">
            <CircleCheck className="size-7" />
          </span>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              {t('auth.register.successTitle')}
            </h1>
            <p className="text-muted-foreground max-w-sm text-sm text-pretty">
              {t('auth.register.successBody')}
            </p>
          </div>
          <Button asChild>
            <Link to="/login">
              <ArrowLeft />
              {t('auth.register.backToSignIn')}
            </Link>
          </Button>
        </div>
        <LoginBrandPanel />
      </div>
    );
  }

  return (
    <div className="grid lg:min-h-svh lg:grid-cols-2">
      <div className="flex min-h-[85svh] flex-col gap-8 p-6 sm:p-10 lg:min-h-0">
        <div className="flex items-center justify-between gap-4">
          {/* The client's own logo, with the monogram as the fallback while
              the config file loads or when a client has not set one. */}
          <ClientLogo className="h-8 max-w-[11rem]" />
          <div className="flex items-center gap-1 sm:gap-2">
            <LanguageSwitcher className="hidden sm:inline-flex" />
            <LanguageMenu className="sm:hidden" />
            <ThemeToggle />
          </div>
        </div>

        {/* The form is taller than the login form, so this column scrolls on its
            own rather than pushing the brand panel around. */}
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-md">
            <div className="mb-8 space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                {t('auth.register.title')}
              </h1>
              <p className="text-muted-foreground text-sm text-pretty">
                {t('auth.register.subtitle')}
              </p>
            </div>

            <form
              noValidate
              onSubmit={handleSubmit((values) => registration.mutate(values))}
              className="space-y-4"
            >
              {registration.isError && (
                <div
                  role="alert"
                  className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-lg border p-3 text-sm"
                >
                  <AlertCircle className="mt-0.5 size-4 shrink-0" />
                  <span>
                    {registration.error instanceof Error
                      ? t(registration.error.message)
                      : t('auth.register.genericError')}
                  </span>
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">
                    {t('auth.register.firstName')}
                  </Label>
                  <Input
                    id="firstName"
                    autoComplete="given-name"
                    autoFocus
                    aria-invalid={Boolean(errors.firstName)}
                    disabled={registration.isPending}
                    {...register('firstName')}
                  />
                  <FieldError message={errors.firstName?.message} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">
                    {t('auth.register.lastName')}
                  </Label>
                  <Input
                    id="lastName"
                    autoComplete="family-name"
                    aria-invalid={Boolean(errors.lastName)}
                    disabled={registration.isPending}
                    {...register('lastName')}
                  />
                  <FieldError message={errors.lastName?.message} />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('common.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder={t('auth.login.emailPlaceholder')}
                  aria-invalid={Boolean(errors.email)}
                  disabled={registration.isPending}
                  {...register('email')}
                />
                <FieldError message={errors.email?.message} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phoneNumber">{t('auth.register.phone')}</Label>
                <Input
                  id="phoneNumber"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder={t('auth.register.phonePlaceholder')}
                  aria-invalid={Boolean(errors.phoneNumber)}
                  disabled={registration.isPending}
                  {...register('phoneNumber')}
                />
                <FieldError message={errors.phoneNumber?.message} />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="gender">{t('auth.register.gender')}</Label>
                  <Controller
                    control={control}
                    name="gender"
                    render={({ field }) => (
                      <Select
                        value={String(field.value)}
                        onValueChange={(value) => field.onChange(Number(value))}
                        disabled={registration.isPending}
                      >
                        <SelectTrigger id="gender">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={String(GENDER.male)}>
                            {t('auth.register.male')}
                          </SelectItem>
                          <SelectItem value={String(GENDER.female)}>
                            {t('auth.register.female')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="language">
                    {t('auth.register.preferredLanguage')}
                  </Label>
                  <Controller
                    control={control}
                    name="language"
                    render={({ field }) => (
                      <Select
                        value={String(field.value)}
                        onValueChange={(value) => field.onChange(Number(value))}
                        disabled={registration.isPending}
                      >
                        <SelectTrigger id="language">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={String(LANGUAGE.en)}>
                            {t('common.english')}
                          </SelectItem>
                          <SelectItem value={String(LANGUAGE.es)}>
                            {t('common.spanish')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('common.password')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="pr-10"
                    aria-invalid={Boolean(errors.password)}
                    disabled={registration.isPending}
                    {...register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    aria-label={
                      showPassword
                        ? t('auth.login.hidePassword')
                        : t('auth.login.showPassword')
                    }
                    className="text-muted-foreground hover:text-foreground absolute inset-y-0 right-0 flex w-10 items-center justify-center"
                  >
                    {showPassword ? (
                      <EyeOff className="size-4" />
                    ) : (
                      <Eye className="size-4" />
                    )}
                  </button>
                </div>

                {/* Requirements are shown up front and tick off live, rather than
                    being revealed one at a time by rejected submissions. */}
                <ul className="grid gap-1 pt-1 sm:grid-cols-2">
                  {PASSWORD_RULES.map((rule) => {
                    const met = rule.test(password ?? '');
                    return (
                      <li
                        key={rule.key}
                        className={cn(
                          'flex items-center gap-1.5 text-xs transition-colors',
                          met ? 'text-success' : 'text-muted-foreground'
                        )}
                      >
                        <Check
                          className={cn(
                            'size-3.5 shrink-0',
                            !met && 'opacity-35'
                          )}
                        />
                        {t(rule.key)}
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">
                  {t('auth.register.confirmPassword')}
                </Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  aria-invalid={Boolean(errors.confirmPassword)}
                  disabled={registration.isPending}
                  {...register('confirmPassword')}
                />
                <FieldError message={errors.confirmPassword?.message} />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={registration.isPending}
              >
                {registration.isPending ? (
                  <>
                    <Loader2 className="animate-spin" />
                    {t('auth.register.submitting')}
                  </>
                ) : (
                  <>
                    <UserPlus />
                    {t('auth.register.submit')}
                  </>
                )}
              </Button>
            </form>

            <p className="text-muted-foreground mt-6 text-center text-sm">
              {t('auth.register.haveAccount')}{' '}
              <Link
                to="/login"
                className="text-foreground font-medium underline-offset-4 hover:underline"
              >
                {t('common.signIn')}
              </Link>
            </p>
          </div>
        </div>
      </div>

      <LoginBrandPanel />
    </div>
  );
}
